import { Suspense, memo, startTransition, useState, useEffect, useRef } from 'react';
import { RouteStateConsumer } from '../prerender/RouteStateConsumer';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'framer-motion';
import { useAuthStore } from '../store/auth.store';
import { changeLanguageLoaded } from '../i18n/config';
import { providerService } from '../services/provider.service';
import { useViewModeStore } from '../store/viewMode.store';
import { Logo } from '../components/common/Logo';
import { ModeToggle } from '../components/ui/ModeToggle';
import { messageService } from '../services/message.service';
import { UserRole } from '../types';
import { motion } from 'framer-motion';
import {
  Menu, X, Home, Search, Briefcase, MessageCircle, User, LogOut, Shield, Wrench,
  LayoutDashboard, Settings, FileText, PenLine, Flag, Headphones, ChevronDown, Globe
} from 'lucide-react';
import { NotificationBell } from '../components/ui/NotificationBell';
import { SocialLinks } from '../components/ui/SocialLinks';
import { InstagramIcon, FacebookIcon } from '../components/icons/BrandIcons';
import { SOCIAL } from '../config/social';
import { EmailVerificationBanner } from '../components/ui/EmailVerificationBanner';
import { MaintenanceNotice } from '../components/common/MaintenanceNotice';
import { SEO } from '../components/SEO';
import { safeLocalStorage } from '../utils/safeStorage';
import { useSEO } from '../hooks/useSEO';
import { toHtmlLang } from '../utils/htmlLang';
import { PageTransition } from '../components/ui/PageTransition';
import {
  getStoredLanguage,
  isLocalizedPublicPath,
  localizePath,
  normalizeLanguage,
  persistLanguage,
  resolveLanguage,
} from '../utils/languageRouting';
import { PrivateLink } from '../components/common/PrivateLink';
import { isRobotsBlockedPath } from '../utils/robotsBlockedPath';

// Links to robots-blocked pages (e.g. /search) render as buttons: see PrivateLink.
const NavTarget = ({ to, className, onClick, children }: { to: string; className?: string; onClick?: () => void; children: React.ReactNode }) =>
  isRobotsBlockedPath(to)
    ? <PrivateLink to={to} className={className} onClick={onClick}>{children}</PrivateLink>
    : <Link to={to} className={className} onClick={onClick}>{children}</Link>;


const setMobileNavigationDocumentState = (isOpen: boolean) => {
  if (typeof document === 'undefined') return;

  document.body.classList.toggle('mobile-navigation-open', isOpen);
  if (isOpen) {
    document.documentElement.setAttribute('data-mobile-navigation-open', 'true');
  } else {
    document.documentElement.removeAttribute('data-mobile-navigation-open');
  }
};

const PAGE_FALLBACK = <div className="min-h-[40vh]" aria-hidden="true" />;

// P1 common: props are stable between MainLayout renders (only the pathname), so React does not treat a
// still-dehydrated <Suspense> boundary as "updated" when post-mount state changes re-render the layout.
const PageArea = memo(function PageArea({ pathname }: { pathname: string }) {
  return (
    <PageTransition shouldAnimate={pathname !== '/'} className="flex-1">
      {/* SEO-R2: lazy route chunks reduce homepage JS parse/execute time for mobile LCP. */}
      <Suspense fallback={PAGE_FALLBACK}>
        <Outlet />
        <RouteStateConsumer />
      </Suspense>
    </PageTransition>
  );
});

export const MainLayout: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, logout } = useAuthStore();
  const { mode } = useViewModeStore();
  const navigate = useNavigate();
  const location = useLocation();

  // Layout-level SEO: only handles noindex for private routes
  // Description is NOT set here — each page provides its own
  const seoProps = useSEO();

  const isAdmin = user?.roles?.includes(UserRole.ROLE_ADMIN);
  const isModerator = user?.roles?.includes(UserRole.ROLE_MODERATOR);
  const isSupport = user?.roles?.includes(UserRole.ROLE_SUPPORT);
  const isProvider = mode === 'provider';
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [providerId, setProviderId] = useState<string | null>(null);
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langMenuRef = useRef<HTMLDivElement | null>(null);
  const langButtonRef = useRef<HTMLButtonElement | null>(null);
  const langMenuId = 'desktop-language-menu';
  const seoPath = (path: string) => localizePath(path, normalizeLanguage(i18n.language));

  useEffect(() => {
    const resolvedLanguage = resolveLanguage(location.pathname, getStoredLanguage());
    if (i18n.language !== resolvedLanguage) {
      void changeLanguageLoaded(resolvedLanguage);
    }
    persistLanguage(resolvedLanguage);
  }, [i18n, location.pathname]);

  // Prevent hydration mismatch: the pre-rendered HTML has no auth state (react-snap
  // runs unauthenticated). Defer auth-dependent rendering until after mount so the
  // initial client render matches the pre-rendered HTML exactly, avoiding error #418.
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => { setHasMounted(true); }, []);

  // P1 common: first render depends only on the URL (banner shown, as in the prerendered HTML);
  // the stored dismissal is applied after mount.
  const [bannerVisible, setBannerVisible] = useState(true);
  useEffect(() => {
    if (safeLocalStorage.getItem('app_banner_dismissed') === '1') setBannerVisible(false);
  }, []);

  // Rotating banner: alternate between the app announcement and the "follow us" message.
  const [bannerSlide, setBannerSlide] = useState(0);
  useEffect(() => {
    if (!bannerVisible) return;
    const id = setInterval(() => setBannerSlide((s) => (s + 1) % 2), 6000);
    return () => clearInterval(id);
  }, [bannerVisible]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setMobileNavigationDocumentState(false);
  }, [location.pathname]);

  useEffect(() => {
    setMobileNavigationDocumentState(isMobileMenuOpen);

    return () => {
      setMobileNavigationDocumentState(false);
    };
  }, [isMobileMenuOpen]);

  // Reset scroll on navigation so SPA route changes behave like normal pages.
  useEffect(() => {
    if (location.hash) {
      requestAnimationFrame(() => {
        const target = document.getElementById(location.hash.slice(1));
        // Fragments can carry data (#service=x) rather than an anchor: then behave like a normal page.
        if (target) target.scrollIntoView({ block: 'start' });
        else window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
      return;
    }

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    if (!isLangOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!langMenuRef.current?.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isLangOpen]);

  // Migrate legacy ?lang= inbound links to crawlable language-prefixed URLs.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const lang = params.get('lang');
    const supported = ['fr', 'ar', 'en', 'darija', 'darija_ar'];
    if (lang && supported.includes(lang)) {
      params.delete('lang');
      const newSearch = params.toString();
      const normalizedLang = normalizeLanguage(lang);
      persistLanguage(normalizedLang);
      void changeLanguageLoaded(normalizedLang);
      const pathname = isLocalizedPublicPath(location.pathname)
        ? localizePath(location.pathname, normalizedLang)
        : location.pathname;
      const cleanUrl = pathname + (newSearch ? `?${newSearch}` : '') + location.hash;
      navigate(cleanUrl, { replace: true });
    }
  }, [i18n, location.hash, location.search, navigate, location.pathname]);

  // Update document direction based on language
  useEffect(() => {
    const rtlLanguages = ['ar', 'darija_ar'];
    document.documentElement.dir = rtlLanguages.includes(i18n.language) ? 'rtl' : 'ltr';
    document.documentElement.lang = toHtmlLang(i18n.language);
  }, [i18n.language]);

  // Fetch unread message count periodically
  useEffect(() => {
    if (!isAuthenticated) return;

    const fetchUnreadCount = async () => {
      if (isAuthenticated) {
        try {
          const count = await messageService.getUnreadCount();
          setUnreadMessageCount(count);

          if (user?.isProvider) {
            try {
              const profile = await providerService.getMyProfile();
              setProviderId(profile.id);
            } catch (e) {
              console.error('Failed to load provider profile ID', e);
            }
          }
        } catch (error) {
          console.error('Failed to fetch unread count:', error);
        }
      }
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);

    return () => clearInterval(interval);
  }, [isAuthenticated, location.pathname, user?.isProvider]);

  const handleLogout = async () => {
    await logout();
    setIsMobileMenuOpen(false);
    setMobileNavigationDocumentState(false);
    navigate('/auth/login');
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setMobileNavigationDocumentState(false);
  };

  const toggleMobileMenu = () => {
    // Render the menu as a transition so the tap paints at once instead of waiting
    // for the whole panel to render and lay out (INP on mobile). The updater keeps
    // rapid taps consistent while a render is pending; the effect on
    // isMobileMenuOpen syncs the document state once the panel commits.
    startTransition(() => setIsMobileMenuOpen((isOpen) => !isOpen));
  };

  const changeLanguage = (lang: string) => {
    const normalizedLanguage = normalizeLanguage(lang);
    persistLanguage(normalizedLanguage);
    void changeLanguageLoaded(normalizedLanguage);
    if (isLocalizedPublicPath(location.pathname)) {
      navigate(`${localizePath(location.pathname, normalizedLanguage)}${location.search}${location.hash}`);
    }
  };

  const NavLink = ({ to, icon: Icon, label, badge }: { to: string; icon: React.ElementType; label: string; badge?: number }) => {
    const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
    return (
      <NavTarget
        to={to}
        onClick={closeMobileMenu}
        className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl transition-all duration-200 relative group ${isActive
          ? 'bg-primary/8 text-primary font-semibold'
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 font-medium'
          }`}
      >
        {isActive && (
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-primary rounded-r-full" />
        )}
        <Icon className={`w-[18px] h-[18px] transition-colors ${isActive ? 'text-primary' : 'text-gray-400 group-hover:text-gray-600'}`} />
        <span className="text-[14px] tracking-tight">{label}</span>
        {badge !== undefined && badge > 0 && (
          <span className="ms-auto bg-red-500 text-white text-[10px] font-bold min-w-[20px] h-5 px-1.5 rounded-full flex items-center justify-center shadow-sm">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </NavTarget>
    );
  };

  // Desktop nav link style
  const DesktopNavLink = ({ to, children, icon: Icon }: { to: string; children: React.ReactNode; icon?: React.ElementType }) => {
    const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
    return (
      <NavTarget
        to={to}
        className={`relative flex items-center gap-2 px-3 py-2 rounded-xl text-[14px] font-medium transition-all duration-200 group ${isActive
            ? 'text-primary bg-primary/8'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
          }`}
      >
        {Icon && <Icon className={`w-4 h-4 transition-colors ${isActive ? 'text-primary' : 'text-muted-foreground/60 group-hover:text-foreground'}`} />}
        <span>{children}</span>
        {isActive && (
          <span className="absolute bottom-0 left-3 right-3 h-[2px] bg-primary rounded-full" />
        )}
      </NavTarget>
    );
  };

  const langLabels: Record<string, { label: string; flag: string }> = {
    fr: { label: 'FR', flag: '🇫🇷' },
    ar: { label: 'AR', flag: '🇸🇦' },
    darija: { label: 'MA', flag: '🇲🇦' },
    darija_ar: { label: 'دارجة', flag: '🇲🇦' },
    en: { label: 'EN', flag: '🇬🇧' },
  };

  // Get navigation links based on current mode
  const getDesktopNavLinks = () => {
    if (!hasMounted || !isAuthenticated) {
      return (
        <>
          <DesktopNavLink to={seoPath('/')} icon={Home}>{t('nav.home')}</DesktopNavLink>
          <DesktopNavLink to="/search" icon={Search}>{t('nav.findPro')}</DesktopNavLink>
          <DesktopNavLink to={seoPath('/guides')} icon={FileText}>{t('nav.guides')}</DesktopNavLink>
          <DesktopNavLink to={seoPath('/devenir-prestataire')} icon={Briefcase}>{t('nav.artisans')}</DesktopNavLink>
          <PrivateLink
            to="/jobs/create"
            className="px-4 py-2 bg-primary text-primary-foreground rounded-full font-medium shadow-md hover:bg-primary/90 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-2 text-[13px]"
          >
            <PenLine className="w-3.5 h-3.5" />
            {t('nav.postRequest')}
          </PrivateLink>
        </>
      );
    }

    if (mode === 'provider' && user?.isProvider) {
      // Provider mode navigation
      return (
        <>
          <DesktopNavLink to={seoPath('/')} icon={Home}>{t('nav.home')}</DesktopNavLink>
          <DesktopNavLink to="/provider/dashboard" icon={LayoutDashboard}>{t('nav.provider.dashboard')}</DesktopNavLink>
          <DesktopNavLink to="/provider/my-jobs" icon={Briefcase}>{t('nav.provider.myJobs')}</DesktopNavLink>
          <DesktopNavLink to="/provider/quotes" icon={FileText}>{t('nav.provider.quotes')}</DesktopNavLink>
          <Link
            to="/messages"
            className={`relative flex items-center gap-2 px-3 py-2 rounded-xl text-[14px] font-medium transition-all duration-200 ${location.pathname === '/messages' || location.pathname.startsWith('/messages/')
              ? 'text-primary bg-primary/8'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
              }`}
          >
            <MessageCircle className="w-4 h-4" />
            {t('nav.messages')}
            {unreadMessageCount > 0 && (
              <span className="absolute -top-1 -right-1 rtl:right-auto rtl:-left-1 bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center shadow-sm ring-2 ring-white">
                {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
              </span>
            )}
          </Link>
        </>
      );
    }

    // Client mode navigation
    return (
      <>
        <DesktopNavLink to={seoPath('/')} icon={Home}>{t('nav.home')}</DesktopNavLink>
        <DesktopNavLink to="/search" icon={Search}>{t('nav.search')}</DesktopNavLink>
        <DesktopNavLink to="/jobs/my" icon={Briefcase}>{t('nav.myJobs')}</DesktopNavLink>
        <Link
          to="/messages"
          className={`relative flex items-center gap-2 px-3 py-2 rounded-xl text-[14px] font-medium transition-all duration-200 ${location.pathname === '/messages' || location.pathname.startsWith('/messages/')
            ? 'text-primary bg-primary/8'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted/60'
            }`}
        >
          <MessageCircle className="w-4 h-4" />
          {t('nav.messages')}
          {unreadMessageCount > 0 && (
            <span className="absolute -top-1 -right-1 rtl:right-auto rtl:-left-1 bg-red-500 text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center shadow-sm ring-2 ring-white">
              {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
            </span>
          )}
        </Link>
        {!user?.isProvider && (
          <Link
            to="/provider/onboarding"
            className="px-4 py-2 bg-primary text-primary-foreground rounded-full font-medium shadow-md hover:bg-primary/90 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 text-[13px]"
          >
            {t('nav.becomeProvider')}
          </Link>
        )}
      </>
    );
  };

  const getMobileNavLinks = () => {
    if (!hasMounted || !isAuthenticated) {
      return (
        <>
          <NavLink to={seoPath('/')} icon={Home} label={t('nav.home')} />
          <NavLink to="/search" icon={Search} label={t('nav.findPro')} />
          <NavLink to={seoPath('/guides')} icon={FileText} label={t('nav.guides')} />
          <NavLink to={seoPath('/devenir-prestataire')} icon={Briefcase} label={t('nav.artisans')} />
          {/* Guest CTA */}
          <PrivateLink
            to="/jobs/create"
            onClick={closeMobileMenu}
            className="flex items-center justify-center gap-2 px-4 py-4 bg-primary text-white rounded-xl font-semibold shadow-md mt-2"
          >
            <PenLine className="w-5 h-5" />
            {t('nav.postRequest')}
          </PrivateLink>
        </>
      );
    }

    if (mode === 'provider' && user?.isProvider) {
      // Provider mode navigation
      return (
        <>
          <NavLink to={seoPath('/')} icon={Home} label={t('nav.home')} />
          <NavLink to="/provider/dashboard" icon={LayoutDashboard} label={t('nav.provider.dashboard')} />
          <NavLink to="/provider/my-jobs" icon={Briefcase} label={t('nav.provider.myJobs')} />
          <NavLink to="/provider/quotes" icon={FileText} label={t('nav.provider.quotes')} />
          <NavLink to="/messages" icon={MessageCircle} label={t('nav.messages')} badge={unreadMessageCount} />
          <NavLink to="/settings" icon={Settings} label={t('settings.title')} />
        </>
      );
    }

    // Client mode navigation
    return (
      <>
        <NavLink to={seoPath('/')} icon={Home} label={t('nav.home')} />
        <NavLink to="/search" icon={Search} label={t('nav.search')} />
        <NavLink to="/jobs/my" icon={Briefcase} label={t('nav.myJobs')} />
        <NavLink to="/messages" icon={MessageCircle} label={t('nav.messages')} badge={unreadMessageCount} />
        {!user?.isProvider && (
          <Link
            to="/provider/onboarding"
            onClick={closeMobileMenu}
            className="flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-xl font-semibold"
          >
            <Wrench className="w-5 h-5" />
            {t('nav.becomeProvider')}
          </Link>
        )}
      </>
    );
  };

  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      {/* Layout-level SEO only marks private routes; pages own canonical/description. */}
      {(seoProps.noindex || seoProps.nofollow) && (
        <SEO noindex={seoProps.noindex} nofollow={seoProps.nofollow} noHreflang={true} />
      )}
      <EmailVerificationBanner />

      {/* Mobile App Announcement Banner */}
      {bannerVisible && (
        <div data-app-banner className="bg-primary text-primary-foreground text-sm py-2 px-4 flex items-center justify-center gap-3 relative">
          <span className="relative flex h-2 w-2 shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-foreground opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-foreground" />
          </span>
          <AnimatePresence mode="wait" initial={false}>
            {bannerSlide === 0 ? (
              <motion.span
                key="banner-app"
                initial={{ y: -4 }}
                animate={{ y: 0 }}
                exit={{ y: 4 }}
                transition={{ duration: 0.4 }}
                className="inline-flex items-center justify-center flex-wrap gap-x-1 gap-y-0.5"
              >
                {t('mobileApp.bannerText')}
                <a href="#app-waitlist" className="underline font-semibold ml-1">
                  {t('mobileApp.bannerCta')}
                </a>
              </motion.span>
            ) : (
              <motion.span
                key="banner-social"
                initial={{ y: -4 }}
                animate={{ y: 0 }}
                exit={{ y: 4 }}
                transition={{ duration: 0.4 }}
                className="inline-flex items-center justify-center flex-wrap gap-x-3 gap-y-0.5"
              >
                <span>{t('social.followUs')}</span>
                <a
                  href={SOCIAL.instagram.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('social.ariaInstagram')}
                  onClick={() => window.gtag?.('event', 'social_click', { network: 'instagram', placement: 'top_banner' })}
                  className="inline-flex items-center gap-1 underline font-semibold"
                >
                  <InstagramIcon className="w-4 h-4" title="" /> {t('social.instagram')}
                </a>
                <a
                  href={SOCIAL.facebook.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('social.ariaFacebook')}
                  onClick={() => window.gtag?.('event', 'social_click', { network: 'facebook', placement: 'top_banner' })}
                  className="inline-flex items-center gap-1 underline font-semibold"
                >
                  <FacebookIcon className="w-4 h-4" title="" /> {t('social.facebook')}
                </a>
              </motion.span>
            )}
          </AnimatePresence>
          <button
            onClick={() => {
              safeLocalStorage.setItem('app_banner_dismissed', '1');
              setBannerVisible(false);
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2 opacity-70 hover:opacity-100 transition-opacity"
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>
      )}

      {/* Header */}
      <header className="bg-card/80 backdrop-blur-xl border-b border-border/50 sticky top-0 z-50 shadow-[0_1px_3px_rgba(0,0,0,0.05)]">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo - Smaller on mobile, larger on desktop */}
            <Link to={seoPath('/')} className="flex items-center shrink-0">
              <span className="sm:hidden">
                <Logo size="sm" />
              </span>
              <span className="hidden sm:block">
                <Logo size="lg" />
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden xl:flex items-center gap-1 overflow-hidden">
              <div className="flex items-center gap-1">
                {getDesktopNavLinks()}
              </div>
              <div className="w-px h-6 bg-border/60 mx-2" />
              {hasMounted && isAdmin && (
                <Link
                  to="/admin"
                  className="px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium text-[13px] hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-1.5"
                >
                  <Shield className="w-3.5 h-3.5" /> Admin
                </Link>
              )}
              {hasMounted && !isAdmin && isModerator && (
                <Link
                  to="/admin"
                  className="px-3 py-1.5 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-lg font-medium text-[13px] hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-1.5"
                >
                  <Flag className="w-3.5 h-3.5" />
                  Modération
                </Link>
              )}
              {hasMounted && !isAdmin && !isModerator && isSupport && (
                <Link
                  to="/admin"
                  className="px-3 py-1.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg font-medium text-[13px] hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex items-center gap-1.5"
                >
                  <Headphones className="w-3.5 h-3.5" />
                  Support
                </Link>
              )}
            </nav>

            {/* Right side - Desktop */}
            <div className="hidden xl:flex items-center gap-2">
              {/* Mode Toggle */}
              <ModeToggle isProvider={hasMounted ? !!user?.isProvider : false} />

              {/* Notification Bell - Desktop */}
              {hasMounted && isAuthenticated && <NotificationBell />}

              {/* Divider */}
              <div className="w-px h-6 bg-border/60 mx-1" />

              {/* Language selector - Custom dropdown */}
              <div
                ref={langMenuRef}
                className="relative"
                onBlur={(event) => {
                  if (!langMenuRef.current?.contains(event.relatedTarget as Node | null)) {
                    setIsLangOpen(false);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    setIsLangOpen(false);
                    langButtonRef.current?.focus();
                  }
                }}
              >
                <button
                  ref={langButtonRef}
                  type="button"
                  onClick={() => setIsLangOpen(!isLangOpen)}
                  aria-haspopup="menu"
                  aria-expanded={isLangOpen}
                  aria-controls={langMenuId}
                  className="flex items-center gap-1.5 px-2.5 py-2 rounded-xl text-[13px] font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all duration-200"
                >
                  <Globe className="w-4 h-4" />
                  <span>{langLabels[i18n.language]?.flag} {langLabels[i18n.language]?.label || 'FR'}</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isLangOpen ? 'rotate-180' : ''}`} />
                </button>
                {isLangOpen && (
                  <div
                    id={langMenuId}
                    role="menu"
                    aria-label={t('nav.language')}
                    className="absolute right-0 rtl:right-auto rtl:left-0 top-full mt-1.5 w-44 bg-card/95 backdrop-blur-xl rounded-xl shadow-xl border border-border/50 py-1.5 z-[60] animate-in fade-in slide-in-from-top-2 duration-200"
                  >
                    {Object.entries(langLabels).map(([code, { label, flag }]) => (
                      <button
                        type="button"
                        key={code}
                        role="menuitemradio"
                        aria-checked={i18n.language === code}
                        onClick={() => { changeLanguage(code); setIsLangOpen(false); }}
                        className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-[13px] transition-colors ${i18n.language === code
                          ? 'text-primary bg-primary/8 font-semibold'
                          : 'text-foreground hover:bg-muted/60'
                          }`}
                      >
                        <span className="text-base">{flag}</span>
                        <span>{label}</span>
                        {i18n.language === code && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-primary" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Auth buttons
                  During hydration (!hasMounted) render the unauthenticated buttons, which
                  matches the react-snap pre-rendered HTML (react-snap runs unauthenticated).
                  After mount, switch to the user dropdown if authenticated. This avoids
                  hydration error #418 caused by a mismatch between pre-rendered and initial
                  client render. */}
              {hasMounted && isAuthenticated ? (
                <div className="relative group">
                  <button className="flex items-center gap-2.5 px-2.5 py-1.5 rounded-xl hover:bg-muted/60 transition-all duration-200">
                    <div className="relative w-8 h-8 overflow-hidden rounded-full bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center text-white text-sm font-bold shadow-sm">
                      <span>{user?.firstName?.charAt(0)?.toUpperCase()}</span>
                      {user?.avatarUrl && (
                        <img
                          src={user.avatarUrl}
                          alt=""
                          referrerPolicy="no-referrer"
                          className="absolute inset-0 h-full w-full object-cover"
                          onError={(event) => event.currentTarget.remove()}
                        />
                      )}
                    </div>
                    <span className="text-foreground font-medium text-[14px]">{user?.firstName}</span>
                    <ChevronDown className="w-3.5 h-3.5 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </button>
                  {/* Dropdown */}
                  <div className="absolute right-0 rtl:right-auto rtl:left-0 top-full mt-1.5 w-60 bg-card/95 backdrop-blur-xl rounded-xl shadow-xl border border-border/50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    {/* User info header */}
                    <div className="px-4 py-3 border-b border-border/50">
                      <p className="text-sm font-semibold text-foreground">{user?.firstName} {user?.lastName}</p>
                      <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                    </div>
                    <div className="py-1.5">
                      {isProvider && providerId && (
                        <Link
                          to={`/providers/${providerId}`}
                          className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-foreground hover:bg-muted/60 transition-colors rounded-lg mx-1.5"
                        >
                          <User className="w-4 h-4 text-muted-foreground" />
                          {t('nav.publicProfile')}
                        </Link>
                      )}
                      <Link
                        to="/settings"
                        className="flex items-center gap-3 px-4 py-2.5 text-[13px] text-foreground hover:bg-muted/60 transition-colors rounded-lg mx-1.5"
                      >
                        <Settings className="w-4 h-4 text-muted-foreground" />
                        {t('settings.title')}
                      </Link>
                    </div>
                    <div className="border-t border-border/50 py-1.5">
                      <button
                        onClick={handleLogout}
                        className="w-[calc(100%-12px)] flex items-center gap-3 px-4 py-2.5 text-[13px] text-destructive hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors rounded-lg mx-1.5"
                      >
                        <LogOut className="w-4 h-4" />
                        {t('auth.logout')}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <PrivateLink
                    to="/auth/login"
                    className="px-4 py-2 text-[13px] text-foreground hover:text-primary font-medium transition-colors rounded-xl hover:bg-muted/60"
                  >
                    {t('auth.login')}
                  </PrivateLink>
                  <PrivateLink
                    to="/register"
                    className="px-4 py-2 text-[13px] text-primary-foreground bg-primary rounded-xl hover:bg-primary/90 transition-all duration-200 shadow-sm hover:shadow-md font-medium"
                  >
                    {t('auth.register')}
                  </PrivateLink>
                </div>
              )}
            </div>

            {/* Mobile: Right side icons */}
            <div className="flex xl:hidden items-center gap-2">
              {/* Mode Toggle - Mobile */}
              <ModeToggle isProvider={hasMounted ? !!user?.isProvider : false} />

              {/* Notification Bell */}
              {hasMounted && isAuthenticated && <NotificationBell />}

              {/* Messages icon with badge */}
              {hasMounted && isAuthenticated && (
                <Link
                  to="/messages"
                  className="relative p-2 text-gray-600 hover:text-primary"
                >
                  <MessageCircle className="w-6 h-6" />
                  {unreadMessageCount > 0 && (
                    <span className="absolute -top-1 -right-1 rtl:right-auto rtl:-left-1 bg-red-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center">
                      {unreadMessageCount > 9 ? '9+' : unreadMessageCount}
                    </span>
                  )}
                </Link>
              )}

              {/* Hamburger menu */}
              <button
                onClick={toggleMobileMenu}
                className="p-2 text-gray-600 hover:text-primary rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay - Premium Design */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <div className="xl:hidden fixed inset-x-0 top-14 z-40 h-[calc(100dvh-3.5rem)]">
              {/* Backdrop with blur */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute inset-0 bg-black/40 backdrop-blur-[6px]"
                onClick={closeMobileMenu}
              />

              {/* Menu Panel - Slides in from right */}
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 28, stiffness: 300 }}
                className="absolute top-0 right-0 rtl:right-auto rtl:left-0 w-[300px] max-w-[85vw] h-full bg-gradient-to-b from-white via-white to-slate-50 shadow-2xl flex flex-col"
              >
                {/* User Header - Gradient Card */}
                {isAuthenticated && user && (
                  <div className="p-5 pb-4">
                    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary via-primary/90 to-primary/70 p-4 shadow-lg">
                      {/* Decorative circles */}
                      <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full bg-white/10" />
                      <div className="absolute -bottom-4 -left-4 w-16 h-16 rounded-full bg-white/5" />

                      <div className="relative flex items-center gap-3.5">
                        <div className="relative w-12 h-12 overflow-hidden rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center ring-2 ring-white/30 shadow-inner">
                          {mode === 'provider' ? <Wrench className="w-5 h-5 text-white" /> : <User className="w-5 h-5 text-white" />}
                          {user.avatarUrl && (
                            <img
                              src={user.avatarUrl}
                              alt=""
                              referrerPolicy="no-referrer"
                              className="absolute inset-0 h-full w-full object-cover"
                              onError={(event) => event.currentTarget.remove()}
                            />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-bold text-white text-[15px] tracking-tight truncate">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-[12px] text-white/70 font-medium">
                            {mode === 'provider'
                              ? t('nav.providerMode')
                              : t('nav.clientMode')}
                          </p>
                        </div>
                      </div>

                      {/* Mode switch button */}
                      {user?.isProvider && (
                        <button
                          onClick={() => {
                            useViewModeStore.getState().toggleMode();
                            closeMobileMenu();
                          }}
                          className="relative mt-3.5 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/20 rounded-xl text-[13px] font-semibold text-white transition-all duration-200 active:scale-[0.97]"
                        >
                          {mode === 'provider' ? (
                            <>
                              <User className="w-3.5 h-3.5" />
                              {t('nav.switchToClient')}
                            </>
                          ) : (
                            <>
                              <Wrench className="w-3.5 h-3.5" />
                              {t('nav.switchToProvider')}
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Navigation Links */}
                <div className="flex-1 overflow-y-auto px-3 pb-3">
                  <nav className="space-y-0.5">
                    {getMobileNavLinks()}
                  </nav>

                  {/* Staff access */}
                  {(isAdmin || isModerator || isSupport) && (
                    <>
                      <div className="mx-3 my-3 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
                      <nav className="space-y-0.5">
                        {isAdmin && (
                          <NavLink to="/admin" icon={Shield} label={t('nav.administration')} />
                        )}
                        {!isAdmin && isModerator && (
                          <NavLink to="/admin/reports" icon={Flag} label={t('nav.moderation')} />
                        )}
                        {!isAdmin && !isModerator && isSupport && (
                          <NavLink to="/admin" icon={Headphones} label={t('nav.support')} />
                        )}
                      </nav>
                    </>
                  )}
                </div>

                {/* Bottom Section - Language + Auth */}
                <div className="max-h-[45%] overflow-y-auto border-t border-gray-100 bg-gradient-to-b from-slate-50/80 to-slate-100/50 px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] space-y-3 flex-shrink-0">
                  {/* Language selector - Minimal pill */}
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                      {t('nav.language')}
                    </span>
                    <select
                      value={i18n.language}
                      onChange={(e) => changeLanguage(e.target.value)}
                      className="flex-1 px-3 py-2 bg-white border border-gray-200/80 rounded-xl text-[13px] font-medium text-gray-700 shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary/30 transition-all appearance-none cursor-pointer"
                    >
                      <option value="fr">🇫🇷 Français</option>
                      <option value="ar">🇸🇦 العربية</option>
                      <option value="darija">🇲🇦 Darija</option>
                      <option value="darija_ar">🇲🇦 دارجة</option>
                      <option value="en">🇬🇧 English</option>
                    </select>
                  </div>

                  {/* Auth actions */}
                  {isAuthenticated ? (
                    <div className="space-y-1.5">
                      <Link
                        to="/settings"
                        onClick={closeMobileMenu}
                        className="flex items-center gap-3 w-full px-3.5 py-2.5 text-gray-600 hover:bg-white hover:text-gray-900 rounded-xl transition-all text-[13px] font-medium"
                      >
                        <User className="w-4 h-4" />
                        {t('nav.myProfile')}
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 w-full px-3.5 py-2.5 text-red-500 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all text-[13px] font-medium"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>{t('auth.logout')}</span>
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <PrivateLink
                        to="/auth/login"
                        onClick={closeMobileMenu}
                        className="flex-1 text-center px-4 py-2.5 border border-gray-200 text-gray-700 rounded-xl font-semibold text-[13px] hover:bg-gray-50 transition-all"
                      >
                        {t('auth.login')}
                      </PrivateLink>
                      <PrivateLink
                        to="/register"
                        onClick={closeMobileMenu}
                        className="flex-1 text-center px-4 py-2.5 bg-primary text-white rounded-xl font-semibold text-[13px] shadow-md hover:shadow-lg transition-all"
                      >
                        {t('auth.register')}
                      </PrivateLink>
                    </div>
                  )}

                  {/* Social links */}
                  <div className="flex items-center justify-between gap-2 pt-1">
                    <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                      {t('social.followUs')}
                    </span>
                    <SocialLinks variant="icon" size="sm" tone="color" />
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-4 py-4 md:py-8 flex-1 w-full flex flex-col">
        <MaintenanceNotice />
        {/* Key ensures component remounts and triggers animation on route change */}
        {/* Explicitly disable animation for Home page ('/') */}
        <PageArea key={location.pathname} pathname={location.pathname} />
      </main>

      {/* Footer */}
      <footer className="bg-foreground text-primary-foreground mt-auto border-t border-border">
        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-muted-foreground">
            <div className="mb-4 md:mb-0 text-center md:text-start">
              <Logo variant="white" size="md" className="mb-2 mx-auto md:mx-0" showSlogan />
              <p className="text-primary-foreground/70">© 2025 Bricolat - {t('footer.allRightsReserved')}</p>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                <a href="#app-waitlist" className="inline-flex items-center gap-2 bg-primary-foreground/10 rounded-xl px-3 py-1.5 text-sm hover:bg-primary-foreground/20 transition-colors text-primary-foreground/80">
                  📱 iOS — <span className="text-primary-foreground/60 text-xs">{t('mobileApp.comingSoon')}</span>
                </a>
                <a href="#app-waitlist" className="inline-flex items-center gap-2 bg-primary-foreground/10 rounded-xl px-3 py-1.5 text-sm hover:bg-primary-foreground/20 transition-colors text-primary-foreground/80">
                  📱 Android — <span className="text-primary-foreground/60 text-xs">{t('mobileApp.comingSoon')}</span>
                </a>
              </div>
              <div className="mt-5 flex flex-col items-center md:items-start gap-2">
                <span className="text-primary-foreground/70 text-sm font-semibold">{t('social.followUs')}</span>
                <SocialLinks variant="icon" size="md" tone="onDark" />
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-2 mt-4 md:mt-0 text-center md:text-start">
              <Link to={seoPath('/services/plomberie')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">Plombier Maroc</Link>
              <Link to={seoPath('/services/electricite')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">Électricien Maroc</Link>
              <Link to={seoPath('/services/peinture')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">Peintre Maroc</Link>
              <Link to={seoPath('/pro/casablanca')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">Artisans Casablanca</Link>
              <Link to={seoPath('/pro/rabat')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">Artisans Rabat</Link>
              <Link to={seoPath('/pro/marrakech')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">Artisans Marrakech</Link>
              <Link to={seoPath('/pro/casablanca/plomberie')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">Plombier Casablanca</Link>
              <Link to={seoPath('/pro/rabat/electricite')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">Électricien Rabat</Link>
              <Link to={seoPath('/blog')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">Guides travaux</Link>
              <Link to={seoPath('/guide/trouver-clients-artisan-maroc')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">Guide artisans</Link>
              <Link to={seoPath('/simulateur-prix')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">{t('footer.priceSimulator')}</Link>
              <Link to={seoPath('/devenir-prestataire')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">Devenir prestataire</Link>
              <Link to={seoPath('/how-it-works')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">{t('home.howItWorks')}</Link>
              <Link to={seoPath('/security')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">{t('nav.security')}</Link>
              <Link to={seoPath('/contact')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">{t('footer.helpContact')}</Link>
              <Link to={seoPath('/privacy')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">{t('footer.privacy')}</Link>
              <Link to={seoPath('/delete-account')} className="min-h-[44px] inline-flex items-center text-primary-foreground/70 hover:text-primary-foreground transition-colors">{t('footer.deleteAccount')}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
